import type { ReactNode } from 'react';

import { Box, Col } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { IconSearch } from '@tabler/icons-react';
import { SocialProofBox } from 'components/landing-pages-v2/SocialProofBox';
import NewSellerFlowAddressInput from 'components/shared/NewSellerFlowAddressInput';

import { isTreatmentAnalyticsId } from '../header/glass/constants';
import { useHeroContext } from './HeroShared';

/** A Tailwind text-color utility (e.g. `text-od-content-inverse-primary`). */
type TextColor = string;

interface HeroTitleAndAddressProps {
  analyticsId?: string;
  title: string;
  subtitle?: string;
  showAddressInput?: boolean;
  showSocialSection?: boolean;
  renderDesktopInput?: () => ReactNode;
  renderMobileInput?: () => ReactNode;
  additionalAddressContent?: ReactNode;
  addressEntryRef: (node?: Element | null) => void;
  // Default input props (used when renderDesktopInput/renderMobileInput are not provided)
  addressCtaText?: string;
  addressCtaTextSmall?: string;
  addressCtaPlaceholderText?: string;
  addressCtaPlaceholderTextSmall?: string;
  trackingKeyword?: string;
  // CTA buttons container props
  showCtaButtonsContainer?: boolean;
  ctaButton1Text?: string;
  ctaButton1Url?: string;
  showCtaButton2?: boolean;
  ctaButton2Text?: string;
  ctaButton2Url?: string;
  textColor?: TextColor;
}

// Internal component: HeroHeadline
interface HeroHeadlineProps {
  title: string;
  subtitle?: string;
  isTreatment?: boolean;
  textColor?: TextColor;
}

const HeroHeadline = ({ title, subtitle, isTreatment, textColor }: HeroHeadlineProps) => {
  return (
    <Col
      className={cn(
        'items-center gap-8',
        isTreatment ? 'md:items-start md:mb-4' : 'lg:items-start',
      )}
    >
      <Text
        as="h1"
        className={cn(
          'text-center text-display-xs',
          isTreatment
            ? 'leading-[0.8] md:text-left'
            : 'md:text-display-sm lg:text-display-md lg:text-left',
          textColor,
        )}
      >
        {title}
      </Text>
      {subtitle && (
        <Text
          as="p"
          className={cn(
            'text-center text-body-md',
            isTreatment ? 'md:text-left md:text-body-lg' : 'md:text-body-lg lg:text-left',
            textColor,
          )}
        >
          {subtitle}
        </Text>
      )}
    </Col>
  );
};

// Internal component: HeroAddressSection
interface HeroAddressSectionProps {
  showAddressInput?: boolean;
  renderDesktopInput: () => ReactNode;
  renderMobileInput: () => ReactNode;
  additionalContent?: ReactNode;
  isTreatment: boolean;
  textColor?: TextColor;
}

const HeroAddressSection = ({
  showAddressInput,
  renderDesktopInput,
  renderMobileInput,
  isTreatment,
  textColor,
}: HeroAddressSectionProps) => {
  if (!showAddressInput) return null;

  return (
    <Col
      className={cn(
        'hp-reskin-v2-address-entry-wrapper z-10 w-full gap-4',
        isTreatment && 'md:mb-4',
      )}
    >
      <Box id="desktop-address-search" className="hidden xl:block">
        {renderDesktopInput()}
      </Box>
      <Box id="mobile-address-search" className="block xl:hidden">
        {renderMobileInput()}
      </Box>
      {isTreatment && (
        <Text
          as="p"
          className={cn(
            'text-center text-body-md md:text-left',
            textColor ?? 'text-od-content-secondary',
          )}
        >
          No obligation. Takes 5 minutes. Your information stays private.
        </Text>
      )}
    </Col>
  );
};

// Internal component: HeroCtaButtonsSection
interface HeroCtaButtonsSectionProps {
  ctaButton1Text?: string;
  ctaButton1Url?: string;
  showCtaButton2?: boolean;
  ctaButton2Text?: string;
  ctaButton2Url?: string;
  analyticsPrefix: string;
}

const HeroCtaButton = ({
  analyticsName,
  label,
  variant,
  href,
}: {
  analyticsName: string;
  label: string;
  variant: 'primary' | 'secondary';
  href?: string;
}) =>
  href ? (
    <Button
      analyticsName={analyticsName}
      aria-label={label}
      size="lg"
      variant={variant}
      href={href}
    >
      {label}
    </Button>
  ) : (
    <Button analyticsName={analyticsName} aria-label={label} size="lg" variant={variant}>
      {label}
    </Button>
  );

const HeroCtaButtonsSection = ({
  ctaButton1Text = 'Missing label',
  ctaButton1Url,
  showCtaButton2,
  ctaButton2Text = 'Missing label',
  ctaButton2Url,
  analyticsPrefix,
}: HeroCtaButtonsSectionProps) => {
  return (
    <Col className="items-center gap-6 lg:flex-row lg:items-start">
      <HeroCtaButton
        analyticsName={`${analyticsPrefix}-cta-button-1`}
        label={ctaButton1Text}
        variant="primary"
        href={ctaButton1Url}
      />
      {showCtaButton2 && (
        <HeroCtaButton
          analyticsName={`${analyticsPrefix}-cta-button-2`}
          label={ctaButton2Text}
          variant="secondary"
          href={ctaButton2Url}
        />
      )}
    </Col>
  );
};

const addressInputCommonProps = (analyticsId?: string, trackingKeyword?: string) => ({
  hideLabel: true,
  inputLocation: 'hero-cta' as const,
  analyticsPrefix: `cosmos-landing-page-hero-v2${analyticsId ? `-${analyticsId}` : ''}`,
  trackingTaxonomy: 'new_homepage' as const,
  trackingKeywords: trackingKeyword,
});

const TreatmentSearchButton = ({
  analyticsPrefix,
  onPress,
  isLoading,
}: {
  analyticsPrefix: string;
  onPress?: () => void;
  isLoading?: boolean;
}) => (
  <Button
    analyticsName={analyticsPrefix}
    aria-label="Search"
    // aria-busy keeps the brand fill (Atoms only greys out a `disabled`
    // button when it is NOT aria-busy); opacity-70 matches the original
    // loading look instead of the default disabled treatment.
    aria-busy={isLoading}
    disabled={isLoading}
    variant="primary"
    onClick={(e) => {
      e.stopPropagation();
      onPress?.();
    }}
    // 18px corners are off the Tailwind radius scale; inline arbitrary value.
    className={cn('h-16 w-15 -mt-5 -mr-4 rounded-[18px] p-0', isLoading && 'opacity-70')}
  >
    <IconSearch size={24} stroke={2} aria-hidden="true" />
  </Button>
);

const DefaultActionButton = ({
  label,
  analyticsPrefix,
  onPress,
  isLoading,
}: {
  label: string;
  analyticsPrefix: string;
  onPress?: () => void;
  isLoading?: boolean;
}) => (
  <Button
    analyticsName={analyticsPrefix}
    aria-label={isLoading ? 'Loading' : label}
    disabled={isLoading}
    size="md"
    variant="primary"
    onClick={(e) => {
      e.stopPropagation();
      onPress?.();
    }}
    // Overlaps the input: fixed 60px height pulled up/right to nest in the field.
    // The half-pixel offsets are off the spacing scale; inline arbitrary values.
    className="h-15 -mt-[17.5px] -mr-[15px]"
  >
    {isLoading ? 'Loading' : label}
  </Button>
);

export const HeroTitleAndAddress = ({
  analyticsId,
  title,
  subtitle,
  showAddressInput,
  showSocialSection,
  renderDesktopInput,
  renderMobileInput,
  additionalAddressContent,
  addressEntryRef,
  addressCtaText = 'Get cash offer',
  addressCtaTextSmall = 'Get offer',
  addressCtaPlaceholderText,
  addressCtaPlaceholderTextSmall,
  trackingKeyword,
  showCtaButtonsContainer,
  ctaButton1Text,
  ctaButton1Url,
  showCtaButton2,
  ctaButton2Text,
  ctaButton2Url,
  textColor,
}: HeroTitleAndAddressProps) => {
  const { analyticsPrefix } = useHeroContext();

  const isTreatment = isTreatmentAnalyticsId(analyticsId);
  const aiCommonProps = addressInputCommonProps(analyticsId, trackingKeyword);

  const DefaultInput = ({ placeholder, ctaLabel }: { placeholder: string; ctaLabel: string }) => (
    <NewSellerFlowAddressInput
      {...aiCommonProps}
      placeholder={placeholder}
      actionButton={(onPress, isLoading) => (
        <DefaultActionButton
          analyticsPrefix={analyticsPrefix}
          label={ctaLabel}
          onPress={onPress}
          isLoading={isLoading}
        />
      )}
    />
  );
  const TreatmentInput = ({ placeholder }: { placeholder: string }) => (
    <NewSellerFlowAddressInput
      {...aiCommonProps}
      placeholder={placeholder}
      actionButton={(onPress, isLoading) => (
        <TreatmentSearchButton
          analyticsPrefix={analyticsPrefix}
          onPress={onPress}
          isLoading={isLoading}
        />
      )}
    />
  );

  const desktopInput = (): ReactNode => {
    if (isTreatment) {
      return (
        <TreatmentInput placeholder={addressCtaPlaceholderText || 'Enter your home address'} />
      );
    }
    if (renderDesktopInput) {
      return renderDesktopInput();
    }
    return (
      <DefaultInput
        placeholder={addressCtaPlaceholderText || 'Enter your home address'}
        ctaLabel={addressCtaText}
      />
    );
  };
  const mobileInput = (): ReactNode => {
    if (isTreatment) {
      return <TreatmentInput placeholder={addressCtaPlaceholderTextSmall || 'Enter an address'} />;
    }
    if (renderMobileInput) {
      return renderMobileInput();
    }
    return (
      <DefaultInput
        placeholder={addressCtaPlaceholderTextSmall || 'Enter an address'}
        ctaLabel={addressCtaTextSmall}
      />
    );
  };

  return (
    <Col
      id="title-and-address-entry"
      className={cn('w-full', isTreatment ? 'gap-4 md:gap-0' : 'gap-8')}
    >
      <HeroHeadline
        title={title}
        subtitle={subtitle}
        isTreatment={isTreatment}
        textColor={textColor}
      />
      <Box className="hidden" ref={(el) => addressEntryRef(el)} />
      <Col className={cn('w-full gap-4', isTreatment ? 'items-start' : 'items-center')}>
        <HeroAddressSection
          showAddressInput={showAddressInput}
          renderDesktopInput={desktopInput}
          renderMobileInput={mobileInput}
          additionalContent={additionalAddressContent}
          isTreatment={isTreatment}
          textColor={textColor}
        />
        <Col className="hidden w-full md:flex lg:hidden">
          {showSocialSection && <SocialProofBox autoplayInterval={3000} />}
        </Col>
      </Col>
      {showCtaButtonsContainer && (
        <HeroCtaButtonsSection
          ctaButton1Text={ctaButton1Text}
          ctaButton1Url={ctaButton1Url}
          showCtaButton2={showCtaButton2}
          ctaButton2Text={ctaButton2Text}
          ctaButton2Url={ctaButton2Url}
          analyticsPrefix={analyticsPrefix}
        />
      )}
    </Col>
  );
};
